import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ComboBox, FlexColumn, FlexRow, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {NovaModel, isNovaModelUnavailable} from '../shared/novaModelList';

interface Props {
	models: NovaModel[];
	// The agent's saved (or draft) model id. Null for an agent that has none yet.
	value: string | null;
	onChange: (modelId: string) => void;
	dataCy: string;
}

// The engineering-owned model list, as a picker. Every entry here is one the service will accept:
// the endpoint withholds retired models and `mapNovaModelList` drops any that arrive anyway, so a
// row can never dead-end the agent it is chosen for. DEPRECATED is offered with a pill — it is
// still saveable, and pushing authors off it is the pill's job, not the filter's.
//
// A saved model that is no longer offered is shown and warned about, never silently replaced with
// the default: the model is part of the version content hash, so repairing the selection here would
// bake a model nobody chose into the next save. Saving is blocked instead — see the Save gate in
// NovaStudioShell, which asks `isNovaModelUnavailable` the same question this does.
export function NovaModelPicker({models, value, onChange, dataCy}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	// `isNovaModelUnavailable`, not a bare lookup: an empty list means the registry read failed, not
	// that every model was retired, and warning on that would tell the author their perfectly current
	// model was gone — on every agent — while offering nothing to replace it with.
	const isUnavailable = isNovaModelUnavailable(models, value);
	// Memoised on `models`: react-stately rebuilds the ComboBox collection whenever the array it is
	// handed changes identity rather than content, and this picker re-renders on every keystroke in
	// the prompt editors above it (they share the draft), so a fresh array each render would rebuild
	// the collection per character typed.
	const items = useMemo(() => models.map(model => ({id: model.modelId, name: model.displayName})), [models]);

	return (
		<FlexColumn gap="xs">
			<ComboBox
				data-cy={dataCy}
				label={formatMessage({id: 'nova.model.label'})}
				placeholder={formatMessage({id: 'nova.model.placeholder'})}
				// `defaultItems`, not `items`: useComboBoxState skips its own filtering entirely when
				// `items` is supplied ("No default filter if items are controlled"), so the box would
				// look searchable — placeholder and all — while typing narrowed nothing and the typed
				// text was reverted on blur. NovaTaskPicker passes `items` on purpose because its
				// matching is server-side; this list is short, local and fixed for the page's lifetime,
				// so the design system's own `contains` filter is exactly what it wants.
				defaultItems={items}
				width={ComboBox.WIDTH.FLEX}
				// `value`/`onChange`, not `selectedKey`/`onSelectionChange`: ComboBox destructures
				// `value` out of its props and assigns `ariaProps.selectedKey = value`
				// unconditionally, so anything passed as `selectedKey` is overwritten with undefined
				// and the box is left uncontrolled.
				value={isUnavailable ? null : value}
				onChange={(key: string | null) => {
					if (key) {
						onChange(key);
					}
				}}
			>
				{(item: {id: string; name: string}) => {
					const model = models.find(candidate => candidate.modelId === item.id);
					// One row, not a stacked name-over-hint: the design system's option is a fixed
					// 44px line with 16px padding and centred content, so a second line has nowhere
					// to go — it overflows into the row above and below and reads as no padding at
					// all. Name, status and guidance sit on the one line the row actually has.
					return (
						<ComboBox.Item key={item.id} textValue={item.name}>
							<FlexRow gap="s" alignItems="center">
								<Text noWrap>{item.name}</Text>
								{model?.lifecycleState === 'DEPRECATED' ? (
									<Pill variant={PillVariant.NEUTRAL}>{formatMessage({id: 'nova.model.deprecated'})}</Pill>
								) : null}
								{model?.hint ? <Text color="auxiliary">{model.hint}</Text> : null}
							</FlexRow>
						</ComboBox.Item>
					);
				}}
			</ComboBox>
			{isUnavailable ? (
				<div data-cy={`${dataCy}-unavailable`} data-testid={`${dataCy}-unavailable`}>
					<FlexColumn gap="xxs" alignItems="flex-start">
						<Text>{formatMessage({id: 'nova.model.unavailableOption'}, {modelId: value})}</Text>
						<Text color="error">{formatMessage({id: 'nova.model.unavailableWarning'})}</Text>
					</FlexColumn>
				</div>
			) : null}
		</FlexColumn>
	);
}

export default NovaModelPicker;
